import { useState, useEffect, useCallback } from 'react';
import { authService } from '../auth-service';
import { AuthState, CurrentUser, LoginCredentials, RegistrationData, PasswordResetData, UserRole } from '../types';

/**
 * Custom hook for authentication state and operations
 */
export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
  });

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const user = await authService.getCurrentUser();
        setAuthState({
          user,
          isLoading: false,
          isAuthenticated: !!user,
          error: null,
        });
      } catch (error) {
        setAuthState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: error instanceof Error ? error.message : 'Failed to initialize auth',
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (credentials: LoginCredentials) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.signIn(credentials);
      
      if (result.requiresPasswordChange) {
        setAuthState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: 'Password change required',
        });
        return { requiresPasswordChange: true, user: result.user };
      }

      const user = await authService.getCurrentUser();
      setAuthState({
        user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
      return { requiresPasswordChange: false, user };
    } catch (error) {
      const errorMessage = (error as any).message || 'Sign in failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Sign up a new user
   */
  const signUp = useCallback(async (data: RegistrationData) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.signUp(data);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return result;
    } catch (error) {
      const errorMessage = (error as any).message || 'Sign up failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Confirm email verification code
   */
  const confirmSignUp = useCallback(async (email: string, code: string) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.confirmSignUp(email, code);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return result;
    } catch (error) {
      const errorMessage = (error as any).message || 'Email confirmation failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Sign out current user
   */
  const signOut = useCallback(async () => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      await authService.signOut();
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = (error as any).message || 'Sign out failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Complete forced password change (first login with temp password)
   */
  const completeNewPassword = useCallback(async (user: any, newPassword: string) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.completeNewPassword(user, newPassword);
      
      // After password change, get updated user
      const updatedUser = await authService.getCurrentUser();
      setAuthState({
        user: updatedUser,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
      return result;
    } catch (error) {
      const errorMessage = (error as any).message || 'Password change failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Initiate forgot password flow
   */
  const forgotPassword = useCallback(async (email: string) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.forgotPassword(email);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return result;
    } catch (error) {
      const errorMessage = (error as any).message || 'Forgot password request failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Confirm forgot password with verification code
   */
  const confirmForgotPassword = useCallback(async (data: PasswordResetData) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.confirmForgotPassword(data);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return result;
    } catch (error) {
      const errorMessage = (error as any).message || 'Password reset failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Change password for authenticated user
   */
  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    setAuthState(prev => ({ ...prev, error: null }));
    
    try {
      const result = await authService.changePassword(oldPassword, newPassword);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return result;
    } catch (error) {
      const errorMessage = (error as any).message || 'Password change failed';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Get current user's role
   */
  const getUserRole = useCallback(async (): Promise<UserRole> => {
    return await authService.getUserRole();
  }, []);

  /**
   * Check if user has required role(s)
   */
  const hasRole = useCallback((requiredRoles: UserRole | UserRole[]): boolean => {
    if (!authState.user) return false;
    
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.includes(authState.user.role);
  }, [authState.user]);

  /**
   * Get access token for API calls
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    return await authService.getAccessToken();
  }, []);

  /**
   * Refresh session tokens
   */
  const refreshSession = useCallback(async () => {
    try {
      return await authService.refreshSession();
    } catch (error) {
      // Silent fail - will be handled on next API call
      return null;
    }
  }, []);

  /**
   * Clear auth state (for debugging/testing)
   */
  const clearAuth = useCallback(async () => {
    setAuthState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    });
    await authService.clearAuthData();
  }, []);

  return {
    // State
    user: authState.user,
    isLoading: authState.isLoading,
    isAuthenticated: authState.isAuthenticated,
    error: authState.error,
    
    // Actions
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    completeNewPassword,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    getUserRole,
    hasRole,
    getAccessToken,
    refreshSession,
    clearAuth,
    
    // Convenience checkers
    isStudent: authState.user?.role === UserRole.STUDENT,
    isParent: authState.user?.role === UserRole.PARENT,
    isTeacher: authState.user?.role === UserRole.TEACHER,
    isAssessmentTeam: authState.user?.role === UserRole.ASSESSMENT_TEAM,
    isVideoTeam: authState.user?.role === UserRole.VIDEO_TEAM,
    isAdmin: authState.user?.role === UserRole.ADMIN,
  };
};

/**
 * Type for the useAuth hook return value
 */
export type UseAuthReturn = ReturnType<typeof useAuth>;